import { module } from "angular";
import "angular-translate";
import type { IIssue, IIssueTypeSiteCount, IResponse } from "App/Modules/Models";
import { IssueSeverity, IssueType, IssueSubType, IssueSourceType, EquipmentClass } from "App/Modules/Models";
import type { Observable } from "rxjs";
import { map } from "rxjs/operators";

import type { IHttpService } from "App/Modules/Services";
import type { IIssuesRepository } from "./IIssuesRepository";

export default class IssuesRepository implements IIssuesRepository {
    public static $inject: string[] = ["mmHttpService", "$translate"];

    private static enhanceIssue(issue: IIssue): IIssue {
        issue.severityName = issue.severity as any as keyof typeof IssueSeverity;
        issue.severity = IssueSeverity[issue.severityName];
        issue.issueSubTypeName = issue.issueSubType as any as keyof typeof IssueSubType;
        issue.issueSubType = IssueSubType[issue.issueSubTypeName];
        issue.issueTypeName = issue.issueType as any as keyof typeof IssueType;
        issue.issueType = IssueType[issue.issueTypeName];
        if (issue.dtIssue && typeof issue.issueDurationSeconds === "number") {
            issue.dtResolved = issue.dtIssue.clone().add(issue.issueDurationSeconds, "seconds");
        }

        if (issue.source) {
            issue.source.typeName = issue.source.type as any as keyof typeof IssueSourceType;
            issue.source.type = IssueSourceType[issue.source.typeName];
        }
        if (issue.affectedMainSiteEquipment) {
            issue.affectedMainSiteEquipment.className = issue.affectedMainSiteEquipment
                .class as any as keyof typeof EquipmentClass;
            issue.affectedMainSiteEquipment.class = EquipmentClass[issue.affectedMainSiteEquipment.className];
        }
        return issue;
    }

    constructor(
        private readonly httpService: IHttpService,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public getActiveIssues(): Observable<IResponse<IIssue[]>> {
        return this.httpService.get<IIssue[]>("issues/active").pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                response.data.forEach((issue) => {
                    IssuesRepository.enhanceIssue(issue);
                    issue.issueTypeNameLocalized = `${this.$translate.instant(
                        `Enums.IssueType.${issue.issueTypeName}`,
                    )} - ${this.$translate.instant(`Enums.IssueSubType.${issue.issueSubTypeName}`)}`;
                    issue.severityNameLocalized = this.$translate.instant(
                        `Entities.Issue.Severity${issue.severityName as string}`,
                    );
                });

                return response;
            }),
        );
    }

    public acknowledgeIssue(id: string): Observable<IResponse<IIssue>> {
        return this.httpService.put<IIssue>(`issues/${id}/acknowledge`, null).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                IssuesRepository.enhanceIssue(response.data);
                response.data.issueTypeNameLocalized = this.$translate.instant(
                    `Enums.IssueType.${response.data.issueTypeName}`,
                );
                response.data.issueSubTypeNameLocalized = this.$translate.instant(
                    `Enums.IssueSubType.${response.data.issueSubTypeName}`,
                );
                response.data.severityNameLocalized = this.$translate.instant(
                    `Entities.Issue.Severity${response.data.severityName}`,
                );

                return response;
            }),
        );
    }

    public getByTimeSpan(params: any): Observable<IResponse<IIssueTypeSiteCount[]>> {
        return this.httpService.get<IIssueTypeSiteCount[]>("issues/sitesstatistics", params);
    }

    public getIssuesForSite(siteId: string, fromDate: string, toDate: string): Observable<IResponse<IIssue[]>> {
        const params = { fromDate, toDate };

        return this.httpService.get<IIssue[]>(`sites/${siteId}/issues`, params).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                response.data.forEach((issue) => {
                    IssuesRepository.enhanceIssue(issue);
                    issue.issueTypeNameLocalized = this.$translate.instant(`Enums.IssueType.${issue.issueTypeName}`);
                    issue.issueSubTypeNameLocalized = this.$translate.instant(
                        `Enums.IssueSubType.${issue.issueSubTypeName}`,
                    );
                    issue.severityNameLocalized = this.$translate.instant(
                        `Entities.Issue.Severity${issue.severityName}`,
                    );
                });

                return response;
            }),
        );
    }
}

module("mmRepositories").service("mmIssuesRepository", IssuesRepository);
